import type { TFunction } from 'i18next';
import type { FlexiblePlacement, Recurrence, RecurrenceDaysOfWeekEnum } from '../../api/generated';
import { DEFAULT_INTERVAL_WEEKS, WEEKDAYS_IN_ORDER, timeWindowParts } from '../Bookings/shared';

const TIME_RANGE_SEPARATOR = ' – ';

// `daysOfWeek` is typed as a Set but arrives as a plain JSON array; Array.from handles both.
const selectedDaysOf = (recurrence: Recurrence): RecurrenceDaysOfWeekEnum[] =>
  Array.from(recurrence.daysOfWeek ?? []);

/**
 * The eligible-days summary in Mon–Sun order, plus "every N weeks" when the interval is more than
 * weekly — e.g. "Man, Ons, Fre" or "Man, Fre · hver 2. uge". These are the days the block MAY land
 * on, not days it occurs on (a recurring reservation is a single block per period).
 */
export const formatEligibleDays = (t: TFunction, recurrence: Recurrence): string => {
  const selected = selectedDaysOf(recurrence);
  const days = WEEKDAYS_IN_ORDER.filter((option) => selected.includes(option.day))
    .map((option) => t(option.shortLabelKey))
    .join(', ');
  const intervalWeeks = recurrence.intervalWeeks ?? DEFAULT_INTERVAL_WEEKS;
  return intervalWeeks > DEFAULT_INTERVAL_WEEKS
    ? `${days} · ${t('booking-every-nth-week', { n: intervalWeeks })}`
    : days;
};

/** The placement window: "2026-06-01 – 2026-12-31". Both bounds are always set for a reservation. */
export const formatPlacementPeriod = (placement: FlexiblePlacement): string =>
  `${placement.windowStart ?? ''}${TIME_RANGE_SEPARATOR}${placement.windowEnd ?? ''}`;

/** The time-of-day constraint: "09:00 – 12:00", or the any-time label when unrestricted. */
export const formatTimeOfDay = (t: TFunction, placement: FlexiblePlacement): string => {
  const window = timeWindowParts(placement.timeOfDayWindow);
  return window
    ? `${window.startTime}${TIME_RANGE_SEPARATOR}${window.endTime}`
    : t('reserved-staff-time-any-time');
};
